import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, Topic, Game } from '../api/rest';
import { currentIdToken, signOut } from '../auth/cognito';
import { relativeTime } from '../lib/time';

export function AdminGames() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [topicSearch, setTopicSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void currentIdToken().then((t) => {
      if (!t) {
        navigate('/admin', { replace: true });
        return;
      }
      void refresh();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const [ts, gs] = await Promise.all([api.listTopics(), api.listGames()]);
      setTopics(ts);
      setGames(gs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  };

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const removeGame = async (g: Game) => {
    if (!confirm(`Delete game ${g.code}? This removes its players, scores and results permanently.`)) return;
    try {
      await api.deleteGame(g.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete game');
    }
  };

  const create = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const game = await api.createGame(selected);
      navigate(`/admin/console/${game.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create game');
    } finally {
      setBusy(false);
    }
  };

  const ts = topicSearch.trim().toLowerCase();
  const visibleTopics = [...topics]
    .filter((t) => t.title.toLowerCase().includes(ts))
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));

  return (
    <div className="stack">
      <div className="rowbetween">
        <h1>Host dashboard</h1>
        <div className="row">
          <Link to="/admin/authoring" className="ghost">Authoring</Link>
          <Link to="/history" className="ghost">History</Link>
          <button className="ghost" onClick={() => signOut().then(() => navigate('/admin'))}>Sign out</button>
        </div>
      </div>

      {error && <div className="banner error">{error}</div>}

      <div className="card">
        <div className="section-head"><h2>New game</h2></div>
        <p className="muted">Tap topics in the order you want to play them.</p>
        {topics.length === 0 ? (
          <p className="muted empty">No topics yet — <Link to="/admin/authoring">create some</Link> first.</p>
        ) : (
          <>
            {topics.length > 6 && (
              <div className="toolbar">
                <div className="searchbox">
                  <span className="searchicon">🔍</span>
                  <input value={topicSearch} onChange={(e) => setTopicSearch(e.target.value)} placeholder="Filter topics…" />
                </div>
              </div>
            )}
            <ul className="selectlist">
              {visibleTopics.map((t) => {
                const idx = selected.indexOf(t.id);
                const rel = relativeTime(t.createdAt);
                return (
                  <li key={t.id} className={idx >= 0 ? 'selected' : ''} onClick={() => toggle(t.id)}>
                    <span className="ttitle">{t.title}</span>
                    {rel && <span className="muted small when">{rel}</span>}
                    {idx >= 0 && <span className="order">#{idx + 1}</span>}
                  </li>
                );
              })}
              {visibleTopics.length === 0 && <li className="muted">No topics match your filter.</li>}
            </ul>
          </>
        )}
        <button className="primary" disabled={selected.length === 0 || busy} onClick={create}>
          {busy ? 'Creating…' : `Create game (${selected.length} topic${selected.length === 1 ? '' : 's'})`}
        </button>
      </div>

      <div className="card">
        <h2>Recent games</h2>
        {games.length === 0 ? (
          <p className="muted">No games yet.</p>
        ) : (
          <ul className="gamelist">
            {games.map((g) => (
              <li key={g.id}>
                <span className="code">{g.code}</span>
                <span className={`status ${g.status}`}>{g.status}</span>
                <span className="muted small when">{relativeTime(g.createdAt) ?? ''}</span>
                {g.status !== 'finished' ? (
                  <Link to={`/admin/console/${g.id}`}>Open console</Link>
                ) : (
                  <Link to="/history">View results</Link>
                )}
                <button className="danger small" onClick={() => removeGame(g)}>Delete</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
